import { useEffect, useRef, useState } from 'react'
import { useObservable } from '@/hooks/rx-hooks'
import { type OutlineViewModel } from '@/features/jobs/drafting/OutlineViewModel'

/**
 * Syncs the URL hash with the active step.
 *
 * @param outline
 */
export function useSyncStepWithHash(outline: OutlineViewModel) {
  const [hash, setHash] = useState(window.location.hash)
  const isFirstRunRef = useRef(true)
  useEffect(() => {
    const handler = () => setHash(window.location.hash)
    window.addEventListener('hashchange', handler)
    return () => {
      window.removeEventListener('hashchange', handler)
    }
  }, [])
  useEffect(() => {
    if (hash) {
      const id = hash.substring(1)
      // The `hash` starts with `#`, so cut that off.
      outline.requestStepWithIdBecomeActive(id)
      // Ensures the hash matches in case nothing was changed.
      const activeId = outline.activeStep?.stepId.toLowerCase()
      if (activeId && activeId !== id) {
        replaceHash(activeId)
      }
    } else {
      outline.requestPendingStepBecomeActive()
    }
  }, [outline, hash])

  useObservable(outline.activeStep$, (step) => {
    if (step) {
      const id = step.stepId.toLowerCase()
      // Writing the hash fires `popstate`, which cancels a pending navigation,
      // such as the one to a draft's own URL right after it's created.
      if (window.location.hash === `#${id}`) {
        isFirstRunRef.current = false
        return
      }

      // This avoids an issue where if you hit the back button it just clears
      // the hash. This is because assigning the hash like we do in the `else`
      // branch will push history.
      if (isFirstRunRef.current && !window.location.hash) {
        replaceHash(id)
      } else {
        window.location.hash = id
      }
      isFirstRunRef.current = false
    }
  })
}

function replaceHash(id: string) {
  window.location.replace(`#${id}`)
}
